import {
  doc,
  collection,
  query,
  orderBy,
  startAt,
  endAt,
  documentId,
  getDoc,
  getDocs,
  getDocFromServer,
  getDocsFromServer,
  setDoc,
  deleteDoc,
  onSnapshot,
  runTransaction,
} from 'firebase/firestore';
import { db } from './firebase';

const LOCAL_PREFIX = 'mytasklog_day_';
const LOCAL_MIGRATED_KEY = 'mytasklog_local_migrated';
const DELETES_LOCAL_KEY = 'mytasklog_pending_deletes';

/** 削除の印を保持しておく期間 */
const TOMBSTONE_DAYS = 30;

/** undefined を含むとFirestoreが受け付けないので除去する */
const clean = (v) => JSON.parse(JSON.stringify(v));

// ==========================================================
// マージ（端末間の衝突を防ぐ）
// ==========================================================
const rank = (t) => {
  if (t.deletedAt) return 3;
  if (t.isCompleted) return 2;
  if (t.isRunning) return 1;
  return 0;
};

export const mergeTasks = (a = [], b = []) => {
  const map = new Map();
  [...a, ...b].forEach((t) => {
    const prev = map.get(t.id);
    if (!prev) {
      map.set(t.id, t);
      return;
    }
    const ta = t.updatedAt || 0;
    const tp = prev.updatedAt || 0;
    if (ta > tp || (ta === tp && rank(t) > rank(prev))) map.set(t.id, t);
  });
  return [...map.values()];
};

export const purgeTombstones = (tasks = []) => {
  const limit = Date.now() - TOMBSTONE_DAYS * 24 * 60 * 60 * 1000;
  return tasks.filter((t) => !t.deletedAt || t.deletedAt > limit);
};

export const needsPush = (merged = [], remote = []) => {
  const rm = new Map(remote.map((t) => [t.id, t]));
  return merged.some((t) => {
    const r = rm.get(t.id);
    if (!r) return true;
    return (t.updatedAt || 0) > (r.updatedAt || 0);
  });
};

// ==========================================================
// ローカル保存
// ==========================================================
export const loadLocalDay = (dateStr) => {
  try {
    return purgeTombstones(JSON.parse(localStorage.getItem(LOCAL_PREFIX + dateStr)) || []);
  } catch {
    return [];
  }
};

export const saveLocalDay = (dateStr, tasks) => {
  if (!tasks || tasks.length === 0) localStorage.removeItem(LOCAL_PREFIX + dateStr);
  else localStorage.setItem(LOCAL_PREFIX + dateStr, JSON.stringify(tasks));
};

/**
 * この端末に記録が残っている日付を、古い順に返す。
 * ログインしたときに、まだ送れていないものを探すのに使う。
 */
export const listLocalDates = () =>
  Object.keys(localStorage)
    .filter((k) => k.startsWith(LOCAL_PREFIX))
    .map((k) => k.slice(LOCAL_PREFIX.length))
    .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
    .sort();

/**
 * ごく初期の形式（全タスクを1つの配列にまとめていたもの）から
 * 日付ごとに分け直す。すでに済んでいれば何もしない。
 */
export const migrateLocalStorage = () => {
  if (localStorage.getItem(LOCAL_MIGRATED_KEY)) return;

  const legacyKeys = ['mytasklog_tasks_v2', 'taskuma_tasks_v2'];
  const raw = legacyKeys.map((k) => localStorage.getItem(k)).find(Boolean);

  if (raw) {
    try {
      const all = JSON.parse(raw) || [];
      const grouped = {};
      all.forEach((t) => {
        if (!t.date) return;
        (grouped[t.date] = grouped[t.date] || []).push({ ...t, updatedAt: t.updatedAt || 1 });
      });
      Object.entries(grouped).forEach(([d, tasks]) => saveLocalDay(d, tasks));
    } catch (e) {
      console.warn('旧データの変換に失敗:', e);
    }
  }
  localStorage.setItem(LOCAL_MIGRATED_KEY, '1');
};

// ==========================================================
// Firestore（タスク）
// ==========================================================
const dayDoc = (uid, dateStr) => doc(db, 'users', uid, 'days', dateStr);
const daysCol = (uid) => collection(db, 'users', uid, 'days');

/**
 * その日の記録を読む。
 *
 * 必ずサーバーから読む。端末に残っているキャッシュが壊れていると、
 * 「文書が無い」と嘘の答えが返り、しかもエラーにならない。
 * 通信できないときだけ、キャッシュに頼る。
 */
export const fetchRemoteDay = async (uid, dateStr) => {
  const ref = dayDoc(uid, dateStr);
  let snap;
  try {
    snap = await getDocFromServer(ref);
  } catch (e) {
    console.warn('サーバーから読めないので手元の控えを使います:', e.message);
    snap = await getDoc(ref);
  }
  return snap.exists() ? purgeTombstones(snap.data().tasks || []) : null;
};

/**
 * その日の記録を保存する。
 *
 * 以前は setDoc で丸ごと置き換えていたが、それだと
 * 手元に無い記録（別の端末が書いたもの、読み込みが間に合わなかったもの）を
 * 削除の印も残さずに消してしまう。
 *
 * そこで、保存のたびに最新を読み、合流させてから書く。
 * 読んで書くまでを一続きの処理（transaction）にしているので、
 * 途中で他の端末が書き込んでも取りこぼさない。
 *
 * 変更はすべて updatedAt が新しくなるので、
 * 合流しても「直したはずが元に戻る」ことは起きない。
 * 削除も、削除の印が新しい記録として残るので消える。
 */
export const saveRemoteDay = async (uid, dateStr, tasks) => {
  const ref = dayDoc(uid, dateStr);
  const incoming = purgeTombstones(tasks || []);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const current = snap.exists() ? snap.data().tasks || [] : [];
    const merged = purgeTombstones(mergeTasks(current, incoming));
    tx.set(ref, { tasks: clean(merged), updatedAt: Date.now() });
  });
};

export const fetchRemoteRange = async (uid, fromStr, toStr) => {
  const q = query(daysCol(uid), orderBy(documentId()), startAt(fromStr), endAt(toStr));
  let snap;
  try {
    snap = await getDocsFromServer(q);
  } catch (e) {
    console.warn('サーバーから読めないので手元の控えを使います:', e.message);
    snap = await getDocs(q);
  }
  const map = {};
  snap.forEach((d) => {
    map[d.id] = purgeTombstones(d.data().tasks || []);
  });
  return map;
};

export const subscribeRemoteDay = (uid, dateStr, onData, onError) =>
  onSnapshot(
    dayDoc(uid, dateStr),
    (snap) => {
      if (snap.metadata.hasPendingWrites) return;
      // 端末の控えから返ってきた「文書なし」は信じない。
      // 控えが壊れていると、空で上書きしてしまう。
      if (snap.metadata.fromCache && !snap.exists()) return;
      onData(snap.exists() ? purgeTombstones(snap.data().tasks || []) : []);
    },
    onError
  );

/** ごく初期の形式（users/{uid}/data/tasks に全部入り）から日付ごとに分け直す */
export const migrateRemoteIfNeeded = async (uid) => {
  const oldRef = doc(db, 'users', uid, 'data', 'tasks');
  const snap = await getDoc(oldRef);
  if (!snap.exists()) return false;

  const all = snap.data().tasks || [];
  const grouped = {};
  all.forEach((t) => {
    if (!t.date) return;
    (grouped[t.date] = grouped[t.date] || []).push({ ...t, updatedAt: t.updatedAt || 1 });
  });

  await Promise.all(
    Object.entries(grouped).map(([d, tasks]) => saveRemoteDay(uid, d, tasks))
  );
  await deleteDoc(oldRef);
  return true;
};

// ==========================================================
// カレンダーの選択（端末間で共有する）
// ==========================================================
const calendarPrefsDoc = (uid) => doc(db, 'users', uid, 'data', 'calendarPrefs');

export const fetchCalendarPrefs = async (uid) => {
  if (!uid) return null;
  try {
    const snap = await getDoc(calendarPrefsDoc(uid));
    if (!snap.exists()) return null;
    const d = snap.data();
    return {
      inputCalendarIds: Array.isArray(d.inputCalendarIds) ? d.inputCalendarIds : [],
      outputCalendarId: d.outputCalendarId || null,
      updatedAt: d.updatedAt || 0,
    };
  } catch (e) {
    console.warn('カレンダー設定の読み込みに失敗:', e.message);
    return null;
  }
};

export const saveCalendarPrefs = async (uid, prefs) => {
  if (!uid) return;
  try {
    await setDoc(calendarPrefsDoc(uid), {
      inputCalendarIds: prefs.inputCalendarIds || [],
      outputCalendarId: prefs.outputCalendarId || null,
      updatedAt: Date.now(),
    });
  } catch (e) {
    console.warn('カレンダー設定の保存に失敗:', e.message);
  }
};

export const subscribeCalendarPrefs = (uid, onData) => {
  if (!uid) return () => {};
  return onSnapshot(
    calendarPrefsDoc(uid),
    (snap) => {
      if (snap.metadata.hasPendingWrites) return;
      if (!snap.exists()) return;
      const d = snap.data();
      onData({
        inputCalendarIds: Array.isArray(d.inputCalendarIds) ? d.inputCalendarIds : [],
        outputCalendarId: d.outputCalendarId || null,
      });
    },
    (e) => console.warn('カレンダー設定の監視でエラー:', e.message)
  );
};

// ==========================================================
// 削除待ちの控え
// ==========================================================
// カレンダーに書き出したイベントを、タスク削除時にすぐ消せなかった場合に備えて
// ここに控えておく。更新ボタンを押したときにまとめて処理する。

const deletesDoc = (uid) => doc(db, 'users', uid, 'data', 'pendingDeletes');

const readLocalDeletes = () => {
  try {
    return JSON.parse(localStorage.getItem(DELETES_LOCAL_KEY)) || [];
  } catch {
    return [];
  }
};

export const loadPendingDeletes = async (uid) => {
  if (!uid) return readLocalDeletes();
  try {
    const snap = await getDoc(deletesDoc(uid));
    return snap.exists() ? snap.data().items || [] : [];
  } catch (e) {
    console.warn('削除待ちの読み込みに失敗:', e.message);
    return readLocalDeletes();
  }
};

export const savePendingDeletes = async (uid, items) => {
  localStorage.setItem(DELETES_LOCAL_KEY, JSON.stringify(items));
  if (!uid) return;
  try {
    await setDoc(deletesDoc(uid), { items: clean(items), updatedAt: Date.now() });
  } catch (e) {
    console.warn('削除待ちの保存に失敗:', e.message);
  }
};

export const addPendingDelete = async (uid, entry) => {
  const cur = await loadPendingDeletes(uid);
  const exists = cur.some(
    (x) => x.eventId === entry.eventId && x.calendarId === entry.calendarId
  );
  if (exists) return;
  await savePendingDeletes(uid, [...cur, entry]);
};

/** 削除を取り消したときに、予約から外す */
export const removePendingDelete = async (uid, entry) => {
  const cur = await loadPendingDeletes(uid);
  const next = cur.filter(
    (x) => !(x.eventId === entry.eventId && x.calendarId === entry.calendarId)
  );
  if (next.length === cur.length) return;
  await savePendingDeletes(uid, next);
};

// ==========================================================
// 最古タスク日付を取得（カレンダー表示用）
// ==========================================================
/**
 * Firestoreから最古のタスク日付を取得
 * ドキュメントID（日付文字列）をクライアント側でソート
 */
export const fetchOldestTaskDate = async (uid) => {
  if (!uid) return null;
  
  try {
    const q = query(collection(db, 'users', uid, 'days'));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) return null;
    
    // ドキュメントID（日付文字列）を配列にしてソート
    const dateIds = snapshot.docs.map(doc => doc.id).sort();
    return dateIds[0];
  } catch (error) {
    console.error('Error fetching oldest task date:', error);
    return null;
  }
};
